/**
 * Wire types for the finance / payouts surface. Hand-written per CLAUDE.md
 * TD-07 (the generated Orval async client is unusable; only its model types
 * are safe, and these endpoints aren't in the spec we generate from yet).
 *
 * All properties `readonly` (CLAUDE.md golden rule 6). Money fields keep the
 * exact server representation — normalize through `./money` before use.
 */

// ── API-FIN-1: pending balances report ──────────────────────────────

export interface PendingBalanceItem {
  readonly venueId: string;
  /**
   * API-FIN-9 identity, verified against the published `PendingBalanceModel`
   * schema (2026-07-01): the item carries `venueName` + `ownerDisplayName`
   * (both nullable, null only if the venue/owner row is missing). There is
   * **no `ownerEmail`** on this endpoint despite the BE prose mentioning it —
   * the schema omits it (flagged to BE).
   */
  readonly venueName: string | null;
  readonly ownerDisplayName: string | null;
  readonly currency: string;
  /** String of **minor units** (BigInt-safe). Normalize via `moneyFromCentsString`. */
  readonly totalPendingCents: string;
  readonly oldestAccrualAt: string;
  readonly accrualCount: number;
  /** Owner has a Stripe Connect account. */
  readonly hasConnect: boolean;
  /** Connect onboarding complete — the balance is payable now. */
  readonly payoutsEnabled: boolean;
}

export interface PendingBalancesResponse {
  readonly items: readonly PendingBalanceItem[];
}

// ── API-FIN-2: pay a venue's pending balance ────────────────────────

export const PAY_OUTCOMES = ["PAID", "CONNECT_INCOMPLETE", "RACE_LOST", "FAILED"] as const;
export type PayOutcome = (typeof PAY_OUTCOMES)[number];

export interface PayResultRow {
  readonly currency: string;
  readonly outcome: PayOutcome;
  /** Minor-units string (confirmed by `PayVenueCurrencyResultModel` schema). */
  readonly amountCents: string;
  readonly accrualCount: number;
  readonly transferId?: string;
}

export interface PayResponse {
  readonly venueId: string;
  /** Empty ⇒ nothing was pending. One row per currency; outcomes can differ. */
  readonly results: readonly PayResultRow[];
}

// ── API-FIN-3: liquidate (write-off) a balance ──────────────────────

export interface LiquidateInput {
  readonly venueId: string;
  readonly currency: string;
  /** Required, 5–500 chars. */
  readonly reason: string;
}

export interface LiquidateResponse {
  readonly venueId: string;
  readonly currency: string;
  /** Minor-units string written off. `"0"` + `accrualCount: 0` = race-lost no-op. */
  readonly liquidatedCents: string;
  readonly accrualCount: number;
}

// ── API-FIN-7: manual ledger corrections (no Stripe) ────────────────

export const LEDGER_CORRECTION_TYPES = ["refund", "chargeback"] as const;
export type LedgerCorrectionType = (typeof LEDGER_CORRECTION_TYPES)[number];

export interface ManualLedgerEntryInput {
  /** Selects the endpoint: `/admin/ledger/refund` or `/admin/ledger/chargeback`. */
  readonly type: LedgerCorrectionType;
  /** Signed minor-units string — must be **negative** for refund/chargeback. */
  readonly amountCents: string;
  /** ISO 4217, uppercase. */
  readonly currency: string;
  /** Required, 5–500 chars. */
  readonly reason: string;
  readonly venueId?: string;
  readonly propertyId?: string;
  readonly subscriptionId?: string;
  readonly stripeReference?: string;
  /** UUID for idempotent retries. */
  readonly idempotencyKey?: string;
}

export interface ManualLedgerEntry {
  readonly id: string;
  readonly entryType: string;
  readonly amountCents: string;
  readonly currency: string;
  readonly occurredAt: string;
  readonly subscriptionId: string | null;
  readonly propertyId: string | null;
  readonly venueId: string | null;
  readonly stripeReference: string | null;
}

// ── API-FIN-4: accrual snapshots (balance detail) ───────────────────

export interface Accrual {
  readonly id: string;
  readonly subscriptionId: string;
  readonly propertyId: string;
  readonly venueId: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly payoutConfigVersionId: string;
  readonly currency: string;
  readonly baseLayerCents: string;
  readonly usageLayerCents: string;
  readonly totalCents: string;
  readonly venueCheckinCount: number;
  readonly totalCheckinsInPool: number;
  readonly eligibleVenueCount: number;
  readonly poolBasisCents: string;
  readonly settledAt: string | null;
  readonly payoutId: string | null;
  readonly liquidationLedgerEntryId: string | null;
  readonly liquidationReason: string | null;
  readonly createdAt: string;
}

export interface AccrualsPageResponse {
  readonly items: readonly Accrual[];
  readonly page: number;
  readonly limit: number;
  readonly totalCount: number;
}

// ── API-FIN-5: cash ledger ──────────────────────────────────────────

/**
 * Known cash-ledger entry types for the filter dropdown. The API enum is
 * **open-ended** (more types exist server-side), so `LedgerEntry.entryType`
 * stays a plain `string` and unknown types render by their raw value.
 */
export const LEDGER_ENTRY_TYPES = [
  "SUBSCRIPTION_PAYMENT_RECEIVED",
  "STRIPE_PROCESSING_FEE",
  "TAX_COLLECTED",
  "VENUE_ACCRUAL",
  "VENUE_TRANSFER",
  "PLATFORM_PAYOUT_TO_BANK",
  "MANUAL_REFUND",
  "CHARGEBACK",
  "WANDERFIT_LIQUIDATION_FROM_STALE_PENDING",
] as const;

export interface LedgerEntry {
  readonly id: string;
  readonly entryType: string;
  /** SIGNED cents string — negatives are outflows. */
  readonly amountCents: string;
  readonly currency: string;
  readonly occurredAt: string;
  readonly subscriptionId: string | null;
  readonly propertyId: string | null;
  readonly venueId: string | null;
  readonly payoutId: string | null;
  readonly accrualSnapshotId: string | null;
  readonly stripeReference: string | null;
  readonly idempotencyKey: string | null;
  readonly description: string;
  readonly createdBy: string | null;
  readonly createdAt: string;
}

export interface LedgerPageResponse {
  readonly items: readonly LedgerEntry[];
  readonly page: number;
  readonly limit: number;
  readonly totalCount: number;
}

// ── API-FIN-6: CFO reports ──────────────────────────────────────────

/**
 * Monthly cash summary. **All amounts are USD cents strings** — the endpoint is
 * a single-currency MVP by design (no `currency` field); render as USD. If the
 * platform ever serves multiple currencies this would mix them and must evolve
 * to currency-keyed maps.
 */
export interface MonthlySummary {
  readonly month: string;
  readonly byEntryType: Readonly<Record<string, string>>;
  readonly byAllocationType: Readonly<Record<string, string>>;
  readonly totalRevenue: string;
  readonly totalFees: string;
  readonly totalTaxes: string;
  readonly totalVenueAccruals: string;
  readonly totalVenueTransfers: string;
  readonly totalRetention: string;
  readonly totalChargebacks: string;
  readonly totalRefunds: string;
  readonly totalLiquidations: string;
  readonly netCashPosition: string;
}

export interface RevenueByCountryRow {
  readonly country: string | null;
  readonly currency: string;
  readonly month: string;
  readonly grossRevenueCents: string;
  readonly taxCollectedCents: string;
  readonly invoiceCount: number;
}

export interface RevenueByCountryResponse {
  readonly from: string;
  readonly to: string;
  readonly rows: readonly RevenueByCountryRow[];
}

// ── API-FIN-8: owner payout history + projection ────────────────────

export const OWNER_PAYOUT_STATUSES = ["pending", "processing", "completed", "failed"] as const;
export type OwnerPayoutStatus = (typeof OWNER_PAYOUT_STATUSES)[number];

export interface OwnerPayout {
  readonly id: string;
  readonly venueId: string;
  readonly venueName: string | null;
  /** **Number in major units** (dollars). Normalize via `moneyFromMajorNumber`. */
  readonly amount: number;
  readonly currency: string;
  readonly status: OwnerPayoutStatus;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly checkinCount: number;
  readonly paidAt: string | null;
}

export interface OwnerPayoutsResponse {
  readonly payouts: readonly OwnerPayout[];
}

export interface OwnerPayoutProjection {
  /** Number in major units. */
  readonly nextPayoutAmount: number;
  readonly nextPayoutDate: string | null;
  readonly currentPeriodCheckinCount: number;
  /** Number in major units. */
  readonly currentPeriodAccrued: number;
  readonly currency: string | null;
}
